import {
  ShieldCheck,
  CheckCircle2,
  Clock,
  Send,
  XCircle,
  AlertTriangle,
  Star,
  CalendarClock,
  MinusCircle,
} from 'lucide-react';
import {
  useApprenantQualiopi,
  type FormStatus,
  type QualiopiForm,
} from '../hooks/useApprenantQualiopi.js';
import { CollapsibleSlice } from '../../../components/CollapsibleSlice.js';

interface Props {
  apprenantId: string;
}

export function QualiteCard({ apprenantId }: Props) {
  const { data: q, isLoading, error } = useApprenantQualiopi(apprenantId);

  if (isLoading || error || !q) {
    return (
      <CollapsibleSlice
        title="Qualité (Qualiopi)"
        icon={<ShieldCheck className="text-primary-500 h-4 w-4" />}
      >
        <div className="text-sm text-neutral-500">{error?.message ?? 'Chargement...'}</div>
      </CollapsibleSlice>
    );
  }

  const completedCount = q.forms.filter((f) => f.status === 'completed').length;
  const overdueCount = q.forms.filter((f) => f.overdueDays && f.overdueDays > 0).length;
  const scoreColor =
    q.overallScore >= 80
      ? 'text-emerald-600'
      : q.overallScore >= 50
        ? 'text-amber-600'
        : 'text-rose-600';

  return (
    <CollapsibleSlice
      title="Qualité (Qualiopi)"
      icon={<ShieldCheck className="text-primary-500 h-4 w-4" />}
      badge={
        <a
          href={`/qualiopi?apprenant=${apprenantId}`}
          className="text-primary-600 text-xs font-medium hover:underline"
        >
          Voir page Qualiopi →
        </a>
      }
      summary={
        <div className="grid grid-cols-4 gap-2">
          <Kpi value={`${q.overallScore}%`} label="Score" color={scoreColor} />
          <Kpi
            value={
              <span className="flex items-center justify-center gap-1">
                <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                {q.satisfactionScore !== null ? q.satisfactionScore.toFixed(1) : '—'}
              </span>
            }
            label="Satisfaction"
          />
          <Kpi
            value={`${completedCount}/${q.forms.length}`}
            label="Complétés"
            color="text-neutral-700"
          />
          <Kpi
            value={String(overdueCount)}
            label="En retard"
            color={overdueCount > 0 ? 'text-rose-600' : 'text-emerald-600'}
          />
        </div>
      }
    >
      {/* Détail collapsible */}
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
        Formulaires F1-F7
      </div>
      <ul className="mb-3 space-y-1">
        {q.forms.map((f) => (
          <FormRow key={f.code} form={f} />
        ))}
      </ul>

      {q.alerts.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
            <AlertTriangle className="h-3 w-3" />
            Alertes
          </div>
          <ul className="space-y-0.5 text-xs text-amber-900">
            {q.alerts.map((alert, i) => (
              <li key={i}>• {alert}</li>
            ))}
          </ul>
        </div>
      )}

      {q.derniereRelance && (
        <div className="mt-3 border-t border-neutral-100 pt-2 text-[10px] text-neutral-500">
          Dernière relance :{' '}
          <span className="font-medium text-neutral-700">
            {new Date(q.derniereRelance).toLocaleDateString('fr-FR')}
          </span>
        </div>
      )}
    </CollapsibleSlice>
  );
}

// ────────────────────────────────────────────────────────────────────

function Kpi({
  value,
  label,
  color = 'text-neutral-900',
}: {
  value: React.ReactNode;
  label: string;
  color?: string;
}) {
  return (
    <div className="text-center">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-neutral-500">{label}</div>
    </div>
  );
}

function FormRow({ form: f }: { form: QualiopiForm }) {
  const isOverdue = f.overdueDays !== null && f.overdueDays > 0;
  const isNotApplicable = f.scheduledSendDate === null && f.status === 'never_sent';

  return (
    <li
      className={`rounded px-2 py-1.5 text-sm transition-colors ${
        isOverdue ? 'bg-rose-50' : 'hover:bg-neutral-50'
      }`}
    >
      <div className="flex items-center gap-2">
        <StatusIcon status={f.status} isOverdue={isOverdue} isNotApplicable={isNotApplicable} />
        <span className="w-7 font-mono text-xs font-bold text-neutral-500">{f.code}</span>
        <span className="flex-1 text-xs text-neutral-700">{f.label}</span>
        {f.score !== null && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
            ★ {f.score.toFixed(1)}
          </span>
        )}
      </div>
      <div className="ml-9 mt-0.5 flex flex-wrap items-center gap-3 text-[10px]">
        {f.scheduledSendDate && (
          <span className="flex items-center gap-1 text-neutral-500">
            <CalendarClock className="h-3 w-3" />
            Prévu :{' '}
            <span className="font-medium">
              {new Date(f.scheduledSendDate).toLocaleDateString('fr-FR')}
            </span>
          </span>
        )}
        {f.sentAt && !f.completedAt && (
          <span className="text-primary-600">
            Envoyé :{' '}
            <span className="font-medium">{new Date(f.sentAt).toLocaleDateString('fr-FR')}</span>
          </span>
        )}
        {f.completedAt && (
          <span className="text-emerald-600">
            Complété :{' '}
            <span className="font-medium">
              {new Date(f.completedAt).toLocaleDateString('fr-FR')}
            </span>
          </span>
        )}
        {isNotApplicable && <span className="italic text-neutral-400">Non applicable</span>}
        {isOverdue && (
          <span className="ml-auto font-semibold text-rose-700">⚠ Retard {f.overdueDays}j</span>
        )}
      </div>
    </li>
  );
}

function StatusIcon({
  status,
  isOverdue,
  isNotApplicable,
}: {
  status: FormStatus;
  isOverdue: boolean;
  isNotApplicable: boolean;
}) {
  if (isNotApplicable) return <MinusCircle className="h-4 w-4 text-neutral-300" />;
  if (isOverdue) return <AlertTriangle className="h-4 w-4 text-rose-500" />;
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    case 'pending':
      return <Clock className="h-4 w-4 text-amber-500" />;
    case 'sent':
      return <Send className="text-primary-500 h-4 w-4" />;
    case 'never_sent':
      return <XCircle className="h-4 w-4 text-neutral-300" />;
    case 'error':
      return <AlertTriangle className="h-4 w-4 text-rose-500" />;
  }
}
